/**
 * Datums- und Wochenhilfen für tagesgenaue Ansichten.
 *
 * Die Praktikumsplanung rechnet in Wochen-Offsets ab dem Beginn eines Zeitraums und
 * bringt dafür eigene Helfer mit. Der Stundenplan arbeitet mit echten Daten, deshalb
 * dieses eigene, kleine Modul statt einer weiteren Kopie in einer Seite.
 */

export const WEEKDAY_NAMES = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];
export const WEEKDAY_SHORT = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

/** "YYYY-MM-DD" eines lokalen Datums, ohne UTC-Versatz. */
export function isoDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** "YYYY-MM-DD" zeitzonenfest einlesen; Zeitanteile werden ignoriert. */
export function parseIso(iso: string): Date {
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** ISO-Wochentag: 1 = Montag ... 7 = Sonntag. */
export function isoWeekday(d: Date): number {
  return d.getDay() === 0 ? 7 : d.getDay();
}

export function mondayOf(d: Date): Date {
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  r.setDate(r.getDate() + (1 - isoWeekday(r)));
  return r;
}

export function addDays(d: Date, days: number): Date {
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  r.setDate(r.getDate() + days);
  return r;
}

/**
 * ISO-Kalenderwoche. Maßgeblich ist der Donnerstag der Woche: er bestimmt sowohl die
 * Nummer als auch das Wochenjahr, das am Jahreswechsel vom Kalenderjahr abweicht.
 */
export function isoWeek(d: Date): { week: number; year: number } {
  const thursday = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  thursday.setUTCDate(thursday.getUTCDate() + 4 - (thursday.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((thursday.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return { week, year: thursday.getUTCFullYear() };
}

/** "04.09.2026"; leere Werte ergeben einen leeren String. */
export function formatDate(iso: string | Date | null | undefined): string {
  if (!iso) return "";
  const d = iso instanceof Date ? iso : parseIso(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

/** "18.09.2026, 08:00:03" in Ortszeit. */
export function formatDateTime(iso: string | Date): string {
  const d = iso instanceof Date ? iso : new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${formatDate(d)}, ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** "04.09." ohne Jahr, für Spaltenköpfe. */
export function formatDayMonth(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.`;
}
